import type { SessionResult } from "../session/gameSession";
import { MODE_BY_ID } from "../state/modeConfig";
import { useTranslation } from "../i18n/useTranslation";

interface ResultsProps {
  modeName: string;
  result: SessionResult;
  level: number;
  xpProgressFrac: number;
  onContinue: () => void;
}

export function Results({
  modeName,
  result,
  level,
  xpProgressFrac,
  onContinue,
}: ResultsProps) {
  const { t } = useTranslation();
  const accuracy = Math.round(
    (result.correctCount / Math.max(1, result.totalRounds)) * 100,
  );
  const xpColor = result.xpEarned >= 0 ? "text-cyan" : "text-red";
  const xpSign = result.xpEarned >= 0 ? "+" : "";

  return (
    <main className="min-h-screen flex flex-col px-5 py-6 max-w-md mx-auto">
      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-fg-3 mb-4">
        {t("results.sessionComplete", { modeName })}
      </span>

      <h1 className="text-3xl md:text-4xl font-medium tracking-[-0.02em] mb-8">
        {t("results.title")}
      </h1>

      <dl className="grid grid-cols-2 gap-y-5 gap-x-4 mb-10">
        <Stat label={t("results.score")} value={result.score.toLocaleString()} accent="cyan" />
        <Stat
          label={t("results.accuracy")}
          value={`${accuracy}%`}
          accent={accuracy >= 80 ? "green" : accuracy >= 60 ? "amber" : "red"}
        />
        <Stat
          label={t("results.xpEarned")}
          value={`${xpSign}${result.xpEarned.toLocaleString()}`}
          accentClass={xpColor}
        />
        <Stat label={t("results.bestStreak")} value={String(result.bestStreak)} accent="purple" />
        <Stat label={t("results.level")} value={String(level)} accent="fg-0" />
        <Stat
          label={t("results.progress")}
          value={`${Math.round(xpProgressFrac * 100)}%`}
          accent="fg-1"
        />
      </dl>

      {result.difficultyIncreased && (
        <p className="text-green text-sm font-medium mb-4">
          {t("results.difficultyUp")}
        </p>
      )}

      {result.newUnlocks.length > 0 && (
        <section className="mb-8">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-purple mb-3 block">
            {t("results.newUnlocks")}
          </span>
          <ul className="flex flex-col gap-2">
            {result.newUnlocks.map((mid) => {
              const cfg = MODE_BY_ID[mid];
              return (
                <li
                  key={mid}
                  className="text-base font-medium tracking-tight"
                  style={{ color: cfg?.iconColor ?? "var(--color-purple)" }}
                >
                  {t(`mode.${mid}.name`)}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <button
        type="button"
        onClick={onContinue}
        className="self-start px-7 py-3 rounded-full bg-cyan text-bg-1 text-sm font-semibold active:scale-95 transition-transform"
      >
        {t("common.back")}
      </button>
    </main>
  );
}

interface StatProps {
  label: string;
  value: string;
  accent?: "cyan" | "green" | "amber" | "red" | "purple" | "fg-0" | "fg-1";
  accentClass?: string;
}

function Stat({ label, value, accent, accentClass }: StatProps) {
  const cls = accentClass ?? (accent ? `text-${accent}` : "text-fg-0");
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-3 mb-1">
        {label}
      </dt>
      <dd className={`text-2xl font-medium tracking-[-0.02em] ${cls}`}>{value}</dd>
    </div>
  );
}
